import { collection, getDocs, onSnapshot } from 'firebase/firestore'
import type { Firestore, QuerySnapshot } from 'firebase/firestore'
import type { SyncHealth } from '@domain/finance'
import type {
  ProviderAccount,
  ProviderAccountRepository,
} from '@domain/ports/finance-repository'
import { HOUSEHOLD_ID } from './household-repository'
import { oneOfOrNull, optionalText, requireText } from './converters'

/**
 * Lê `providerAccounts` — o catálogo do que a Pluggy enxerga, publicado pelo
 * sync a cada execução.
 *
 * **Só leitura, e o adapter nem oferece escrita.** `firestore.rules` nega, e
 * declarar um `create` aqui daria uma porta cujo único desfecho é
 * `permission-denied` em runtime — a mesma armadilha que fez `createManual` de
 * transação ficar de fora na Fase 2.
 */
const providerAccountsPath = (): string => `households/${HOUSEHOLD_ID}/providerAccounts`
const syncHealthPath = (): string => `households/${HOUSEHOLD_ID}/syncHealth`

export function createProviderAccountRepository(db: Firestore): ProviderAccountRepository {
  return {
    async list() {
      // **Sem `query()`/`orderBy`, e a ordenação acontece aqui.**
      //
      // A primeira versão usava `orderBy('name')` no servidor, e
      // `firestore-indexes.test.ts` reprovou — ele conta os `query()` de `src/`
      // e exige que cada um esteja registrado para ter o índice derivado e
      // conferido. A trava está certa, e a resposta dela aqui é que **não deve
      // haver consulta**: este catálogo tem uma linha por conta do provider —
      // duas, hoje — e mandar o Firestore ordenar isso é peso de índice sem
      // ganho nenhum.
      //
      // A ordem importa porque a lista é escolhida por gente, e a ordem natural
      // de documento é o id, que aqui é um UUID.
      const snap = await getDocs(collection(db, providerAccountsPath()))
      return snap.docs
        .map((d) => toProviderAccount(d.id, d.data()))
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    },

    // Sem `query()`, pelo mesmo motivo de `list`: um documento por item do
    // provider — um, hoje. Ao vivo porque o aviso tem de sumir sozinho quando
    // os dados voltam, sem a pessoa recarregar.
    observeHealth: (onChange, onError) =>
      onSnapshot(
        collection(db, syncHealthPath()),
        (snap: QuerySnapshot) => {
          onChange(snap.docs.map((d) => toSyncHealth(d.id, d.data())))
        },
        onError,
      ),
  }
}

/**
 * O resumo que o sync publica. **Estado desconhecido lê como `ok`**: o aviso
 * existe para ser levado a sério, e um aviso falso — por um valor que alguém
 * escreveu à mão, ou por um vocabulário velho — ensina a ignorá-lo.
 */
export function toSyncHealth(itemId: string, data: Record<string, unknown>): SyncHealth {
  return {
    itemId,
    status: oneOfOrNull(data['status'], ['ok', 'stale', 'error'] as const) ?? 'ok',
    dataUpTo: optionalText(data['dataUpTo']),
    problemSince: optionalText(data['problemSince']),
    checkedAt: optionalText(data['checkedAt']),
  }
}

function toProviderAccount(id: string, data: Record<string, unknown>): ProviderAccount {
  const where = `providerAccounts/${id}`
  return {
    externalId: requireText(data['externalId'], `${where}.externalId`),
    itemId: requireText(data['itemId'], `${where}.itemId`),
    kind: data['kind'] === 'creditCard' ? 'creditCard' : 'account',
    name: requireText(data['name'], `${where}.name`),
    institution: requireText(data['institution'], `${where}.institution`),
    lastFourDigits: optionalText(data['lastFourDigits']),
  }
}
